import React from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const revealItem = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

const CAPABILITIES = [
  {
    id: "strategy",
    name: "Strategy & Creative",
    subs: ["Concepts", "Creative Direction", "Graphic Design", "3D", "Spatial & Retail Design"],
  },
  {
    id: "activations",
    name: "Brand Activations",
    subs: ["On-ground Activations", "Pop-ups", "Roadshows", "Sampling", "Consumer Experiences"],
  },
  {
    id: "events",
    name: "Events & Experiential IPs",
    subs: ["Conferences", "Summits", "Launches", "Networking", "Live IPs", "Entertainment"],
  },
  {
    id: "production",
    name: "Production & Fabrication",
    subs: ["Stage", "Scenic", "Fabrication", "AV", "Event Infrastructure", "Permits & Safety"],
  },
  {
    id: "technology",
    name: "Technology & Immersive Experiences",
    subs: ["AR/VR", "Holograms", "Interactive Installations", "Digital Experiences", "Hybrid Events"],
  },
  {
    id: "digital",
    name: "Digital & Content",
    subs: ["Pre-buzz", "Influencers", "Social Content", "Paid Media", "Corporate Films", "Short Films", "Event Content"],
  },
  {
    id: "government",
    name: "Government & Large-Scale Events",
    subs: ["Government Events", "Public Outreach", "Investor Summits", "Biennales", "Large-Scale Exhibitions"],
  },
];

export default function About() {
  return (
    <section
      id="studio"
      data-testid="about-section"
      className="shell py-14 md:py-20 bg-[color:var(--collab-forest)]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Brand positioning statement — compact */}
        <motion.h2
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={revealItem}
          className="font-display font-light text-[22px] md:text-[23px] lg:text-[26px] leading-[1.5] tracking-[-0.01em] text-[color:var(--collab-cream)] w-full text-center pretty"
        >
          We are an{" "}
          <em className="italic font-medium text-[color:var(--collab-lime)]">
            experiential marketing agency
          </em>{" "}
          devoted to elevating customer engagement. From tech showcases
          to entertainment spectacles, pan-India, alongside brand
          pop-ups, sports events, music festivals and live events. We
          transform ideas into{" "}
          <em className="italic font-medium text-[color:var(--collab-lime)]">
            immersive brand worlds
          </em>{" "}
          that captivate audiences and deliver measurable impact.
        </motion.h2>

        {/* Seven capabilities — same scroll, compact editorial grid */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-12 md:mt-16 border-t border-[rgba(245,241,232,0.22)] pt-8 md:pt-10 text-center font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] text-[color:var(--collab-cream)]"
        >
          Our{" "}
          <em className="italic font-medium text-[color:var(--collab-lime)]">
            Capabilities
          </em>
          <span
            aria-hidden
            className="block mx-auto mt-4 md:mt-5 w-16 md:w-20 h-[2px] bg-[color:var(--collab-lime)]"
          />
        </motion.h3>
        <div
          className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 md:gap-y-10"
          data-testid="capabilities-list"
        >
          {CAPABILITIES.slice(0, 4).map((cap, i) => (
            <motion.div
              key={cap.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.05 }}
              className="text-center border-t border-[rgba(245,241,232,0.16)] pt-6 lg:pt-0 lg:border-t-0 lg:border-l lg:first:border-l-0 lg:pl-8 lg:first:pl-0"
              data-testid={`cap-row-${cap.id}`}
            >
              <h4 className="font-mono text-lg md:text-xl uppercase tracking-[0.18em] leading-[1.4] text-[color:var(--collab-cream)]">
                {cap.name}
              </h4>
              <p className="mt-3 text-[17px] md:text-[19px] font-light leading-[1.6] text-[rgba(245,241,232,0.6)]">
                {cap.subs.join(" · ")}
              </p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-8 md:gap-y-10 max-w-6xl mx-auto">
          {CAPABILITIES.slice(4).map((cap, i) => (
            <motion.div
              key={cap.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: EASE, delay: (i + 4) * 0.05 }}
              className="text-center border-t border-[rgba(245,241,232,0.16)] pt-6 lg:pt-0 lg:border-t-0 lg:border-l lg:first:border-l-0 lg:pl-8 lg:first:pl-0"
              data-testid={`cap-row-${cap.id}`}
            >
              <h4 className="font-mono text-lg md:text-xl uppercase tracking-[0.18em] leading-[1.4] text-[color:var(--collab-cream)]">
                {cap.name}
              </h4>
              <p className="mt-3 text-[17px] md:text-[19px] font-light leading-[1.6] text-[rgba(245,241,232,0.6)]">
                {cap.subs.join(" · ")}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
